import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { sleep } from '@/utils/helpers';
import { Observable } from "rxjs";

import { createUserWithEmailAndPassword } from '@firebase/auth';
import {
    User,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signInWithPopup
} from 'firebase/auth';
import { GoogleAuthProvider } from 'firebase/auth';
import { firebaseAuth } from '@/firebase';
import { FormGroup } from "@angular/forms";
import { LocalDataServiceService } from './local-data-service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ProductDetails } from '@/modals/product-details';
import { ConfigService } from './config.service';
import { CategoryDetails } from '@/modals/category-details';
import { Enquiry } from "@/models/enquiry/Enquiry";
import { BillingDetails } from "@/models/billing/BillingDetails";
import { AddNewProduct } from '@/models/add-new-product/add-new-product';
import { ImageDetails } from '@/models/image-details/image-details';
import { EnquiryDetails } from '@/modals/enquiry-details';
import { RegistrationDetails } from '@/models/login/registrationDetails';

const provider = new GoogleAuthProvider();

@Injectable({
    providedIn: 'root'
})
export class AppService {
    public user?: User | null = null;
    private endPoint: string;

    constructor(
        private router: Router,
        private toastr: ToastrService,
        private localData: LocalDataServiceService,
        private http: HttpClient,
        private configService: ConfigService
    ) {
        onAuthStateChanged(
            firebaseAuth,
            (user) => {
                if (user) {
                    this.user = user;
                } else {
                    this.user = undefined;
                }
            },
            (e) => {
                this.user = undefined;
            }
        );
    }

    async registerWithEmail(email: string, password: string) {
        try {
            const result = await createUserWithEmailAndPassword(
                firebaseAuth,
                email,
                password
            );
            this.user = result.user;
            this.router.navigate(['/']);
            return result;
        } catch (error) {
            this.toastr.error(error.message);
        }
    }

    async loginWithEmail(email: string, password: string) {
        try {
            const result = await signInWithEmailAndPassword(
                firebaseAuth,
                email,
                password
            );
            this.user = result.user;
            this.router.navigate(['/']);

            return result;
        } catch (error) {
            this.toastr.error(error.message);
        }
    }

    async signInByGoogle() {
        try {
            const result = await signInWithPopup(firebaseAuth, provider);
            this.user = result.user;
            console.log(result);
            this.router.navigate(['/main/dashboard']);

            return result;
        } catch (error) {
            this.toastr.error(error.message);
        }
    }

    async getProfile() {
        try {
            await sleep(500);
            const user = firebaseAuth.currentUser;
            if (user) {
                this.user = user;
            } else {
                this.logout();
            }
        } catch (error) {
            this.logout();
            this.toastr.error(error.message);
        }
    }

    async logout() {
      localStorage.clear();
        this.router.navigate(['/login']);
    }

    saveUserRegisteredDetails(userData: FormGroup): Observable<any> {
        return this.http.post(
            this.configService.getConfig().END_POINT + "/auth/registration", userData);
    }

    loginUserData(login: FormGroup): Observable<any> {
        return this.http.post(
            this.configService.getConfig().END_POINT + "/auth/login", login);
    }

    saveNewProduct(formGroup: FormGroup[]) {
        return this.http.post<AddNewProduct>(
            this.configService.getConfig().END_POINT + "/api/product/add-all", formGroup,
            { headers: this.localData.getHeaders() }
        );
    }

    //Get all category details
    getAllCategoryDetails() {
        return this.http.get<CategoryDetails[]>(
            this.configService.getConfig().END_POINT + "/api/category/get-all",
            { headers: this.localData.getHeaders() }
        );
    }

    // Get-All Products For Website
    getAllProducts() {
        return this.http.get<ProductDetails[]>(
            this.configService.getConfig().END_POINT + "/api/product/get-all"
        );
    }


    // category Get Details
    getCategoryDetails() {
        return this.http.get<CategoryDetails[]>(
            this.configService.getConfig().END_POINT + "/api/category/get-all"
        );
    }

    // Update product availability status
    updateProductAvailability(jsonData: any) {
        return this.http.put(
            this.configService.getConfig().END_POINT + "/api/product/update-availability", jsonData,
            { headers: this.localData.getHeaders() }
        );
    }

    //get all enquiry list by admin
    getAllEnquiryList() {
        return this.http.get(
            this.configService.getConfig().END_POINT + "/api/enquiry/get-all",
            { headers: this.localData.getHeaders() }
        )
    }

    //update customer enquiry status by ADMIN
    updateEnquiryStatusByEnquiryId(updateStatus: any) {
        return this.http.put<Enquiry>(
            this.configService.getConfig().END_POINT + "/api/enquiry/update-status", updateStatus,
            { headers: this.localData.getHeaders() }
        )
    }

    getAllProductsByProductCode(productCode: any) {
        return this.http.get<ProductDetails[]>(
            this.configService.getConfig().END_POINT + "/api/product/get-all" + "/" + productCode,
            { headers: this.localData.getHeaders() }
        )
    }

    //save bill details
    saveInvoiceDetails(formGroup: FormGroup) {
        return this.http.post<BillingDetails>(
            this.configService.getConfig().END_POINT + "/api/billing/save", formGroup,
            { headers: this.localData.getHeaders() }
        )
    }

    //get bill details by BillId
    getBillDetailById(billId: number) {
        return this.http.get<BillingDetails>(
            this.configService.getConfig().END_POINT + "/api/billing/get/" + billId,
            { headers: this.localData.getHeaders() }
        )
    }

    //Get product id details
    getProductIdByDetails(productId: number) {
        return this.http.get<AddNewProduct>(
            this.configService.getConfig().END_POINT + "/api/product/get-by-id/" + productId,
            { headers: this.localData.getHeaders() }
        )
    }

    //Image Id based on delete the image
    deleteImageByAssetId(imageId: number) {
        return this.http.delete(
            this.configService.getConfig().END_POINT + "/api/product/image-details/delete/" + imageId,
            { headers: this.localData.getHeaders() }
        )
    }

    //Update product details
    updateProductDetails(formGroup: FormGroup) {
        return this.http.put<AddNewProduct>(
            this.configService.getConfig().END_POINT + "/api/product/update/", formGroup,
            { headers: this.localData.getHeaders() }
        )
    }

    //Delete product variant details based on
    deleteProductVariantDetails(variantId: number) {
        return this.http.delete(
            this.configService.getConfig().END_POINT + "/api/product/variant/delete/" + variantId,
            { headers: this.localData.getHeaders() }
        )
    }

    //update product image
    updateProductImage(productId: number, ImageList: ImageDetails[]) {
        return this.http.post<any>(
            this.configService.getConfig().END_POINT + "/api/product/image-details/add-more/" + productId, ImageList,
            { headers: this.localData.getHeaders() }
        )
    }

    //Get all bill details
    getAllBillDetails() {
        return this.http.get<BillingDetails[]>(
            this.configService.getConfig().END_POINT + "/api/billing/get-all",
            { headers: this.localData.getHeaders() }
        )
    }

    // send Enquiry
    sendEnquiry(data: EnquiryDetails) {
        return this.http.post(
            this.configService.getConfig().END_POINT + "/api/enquiry/send", data
        );
    }

    // get product by Id
    getProductById(id: number) {
        return this.http.get<ProductDetails>(
            this.configService.getConfig().END_POINT + "/api/product/get-by-id/"+ id
        );
    }

     // get userDetails by Token
    getUserDetails() {
        return this.http.get<RegistrationDetails>(
            this.configService.getConfig().END_POINT + "/api/registration/get",
             { headers: this.localData.getHeaders() }
        );
    }

    // update user details
     updateWhatsAppNumber(requestBody) {
        return this.http.put<RegistrationDetails>(
            this.configService.getConfig().END_POINT + "/api/registration/update" , requestBody,
            { headers: this.localData.getHeaders() }
        )
    }

}


